// Campaign BGM — six mastered tracks (인트로 / 나래이션-동료·적 / 보드게임-동료·적 /
// 엔딩), switched by Game.tsx as the campaign moves through scenes (see its
// scene→track grouping). Match tracks (동료/적 보드게임) loop; the rest play once
// and fall silent. Uses Web Audio rather than <audio loop> so a match loop's
// seam crossfades instead of jumping straight from the track's silent tail
// back to its loud head (보드게임-동료(1막-3막) starts loud, ends in silence).

import { getAudioCtx } from "./context.ts";

export type BgmId =
  | "intro"
  | "narrationAlly"
  | "narrationVillain"
  | "matchAlly"
  | "matchVillain"
  | "ending";

interface TrackDef {
  src: string;
  loop: boolean;
}

const TRACKS: Record<BgmId, TrackDef> = {
  intro: { src: "/assets/audio/bgm-intro.mp3", loop: false },
  narrationAlly: { src: "/assets/audio/bgm-narration-ally.mp3", loop: false },
  narrationVillain: { src: "/assets/audio/bgm-narration-villain.mp3", loop: false },
  matchAlly: { src: "/assets/audio/bgm-match-ally.mp3", loop: true },
  matchVillain: { src: "/assets/audio/bgm-match-villain.mp3", loop: true },
  ending: { src: "/assets/audio/bgm-ending.mp3", loop: false },
};

const SWITCH_FADE_S = 0.8; // crossfade when switching to a different track
const LOOP_CROSSFADE_S = 1.5; // overlap between one loop pass and the next

/** Schedules back-to-back passes of one loop buffer, overlapping each by
 *  `crossfadeSec` so the next pass's head fades in under the previous pass's
 *  tail instead of cutting straight from one to the other. */
class LoopScheduler {
  private readonly crossfadeSec: number;
  private timers: number[] = [];
  private stopped = false;
  private nextStart = 0;

  constructor(
    private ctx: AudioContext,
    private dest: AudioNode,
    private buffer: AudioBuffer,
    crossfadeSec: number,
  ) {
    this.crossfadeSec = Math.min(crossfadeSec, buffer.duration / 3);
  }

  start(at: number): void {
    this.nextStart = at;
    this.scheduleNext(true);
  }

  private scheduleNext(first: boolean): void {
    if (this.stopped) return;
    const { ctx, buffer, crossfadeSec: cf } = this;
    const start = this.nextStart;
    const dur = buffer.duration;

    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const gain = ctx.createGain();
    src.connect(gain).connect(this.dest);

    if (!first) {
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(1, start + cf);
    }
    gain.gain.setValueAtTime(1, start + dur - cf);
    gain.gain.linearRampToValueAtTime(0, start + dur);

    src.start(start);
    src.stop(start + dur + 0.05);

    const next = start + dur - cf;
    this.nextStart = next;
    const delayMs = Math.max(0, next - ctx.currentTime - 0.3) * 1000;
    this.timers.push(window.setTimeout(() => this.scheduleNext(false), delayMs));
  }

  /** Stops scheduling further passes; whatever segment is already playing
   *  rides out under the outer track-switch fade instead of cutting sharply. */
  stop(): void {
    this.stopped = true;
    for (const t of this.timers) clearTimeout(t);
    this.timers = [];
  }
}

class Bgm {
  private buffers = new Map<BgmId, Promise<AudioBuffer>>();
  private activeId: BgmId | null = null;
  private activeTrackGain: GainNode | null = null;
  private activeLoop: LoopScheduler | null = null;
  private token = 0;

  private loadBuffer(ctx: AudioContext, id: BgmId): Promise<AudioBuffer> {
    let p = this.buffers.get(id);
    if (!p) {
      p = fetch(TRACKS[id].src)
        .then((r) => r.arrayBuffer())
        .then((arr) => ctx.decodeAudioData(arr));
      this.buffers.set(id, p);
    }
    return p;
  }

  /** Switches to `id`, crossfading from whatever is currently playing. A call
   *  for the track already active is a no-op — re-entering a scene group that
   *  shares a track lets it keep playing (or stay silent, once its one pass
   *  has finished) instead of restarting it. */
  play(id: BgmId): void {
    if (this.activeId === id) return;
    this.activeId = id;
    const myToken = ++this.token;
    const { ctx, master } = getAudioCtx();
    if (ctx.state === "suspended") ctx.resume().catch(() => {});

    this.loadBuffer(ctx, id).then((buffer) => {
      if (myToken !== this.token) return; // superseded while decoding

      const prevGain = this.activeTrackGain;
      const prevLoop = this.activeLoop;

      const trackGain = ctx.createGain();
      trackGain.gain.value = 0;
      trackGain.connect(master);

      const now = ctx.currentTime;
      trackGain.gain.linearRampToValueAtTime(1, now + SWITCH_FADE_S);

      let loop: LoopScheduler | null = null;
      if (TRACKS[id].loop) {
        loop = new LoopScheduler(ctx, trackGain, buffer, LOOP_CROSSFADE_S);
        loop.start(now);
      } else {
        const src = ctx.createBufferSource();
        src.buffer = buffer;
        src.connect(trackGain);
        src.start(now);
      }

      this.activeTrackGain = trackGain;
      this.activeLoop = loop;

      if (prevGain) {
        prevGain.gain.cancelScheduledValues(now);
        prevGain.gain.setValueAtTime(prevGain.gain.value, now);
        prevGain.gain.linearRampToValueAtTime(0, now + SWITCH_FADE_S);
        prevLoop?.stop();
        window.setTimeout(() => prevGain.disconnect(), (SWITCH_FADE_S + 0.2) * 1000);
      }
    });
  }
}

export const bgm = new Bgm();
